import api from 'api';
import {
  HAS_SEEN_AI_IGNORED_MEMORIES_HINT,
  TIMELINE_pixelsPerMinute,
  TIMESTAMP_SNAPPING_MINUTES,
} from 'constants/user';
import { intersection } from 'lodash-es';
import { DateTime } from 'luxon';
import { showToast } from 'redux/modules/toasts.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import type { TimelineFilterToRemember } from 'redux/modules/user.types';
import { isTimelineFilterToRemember } from 'redux/modules/user.types';
import { getExcludeTrackerEntriesFromTimelyApiRequest } from 'redux/selectors/development';
import {
  getFilter,
  getLastPusherUpdate,
  getMemoryGroups,
  getTimelineResultMeta,
  getTimelineSelection,
  getTimelineXhrStatusStateByKey,
} from 'redux/selectors/timeline';
import {
  getIsSelectedDayWithinMemoryHistoryDaysLimit,
  getTimelineMemories,
} from 'redux/selectors/timeline.memories';
import { getUserPropertyAsNumber } from 'redux/selectors/user';
import { TrackingActions } from 'tracking/actions';
import type { PusherMessage } from 'types/pusher';
import type { Thunk, ThunkDispatch } from 'types/redux';
import type { ValidGroupStatus } from 'types/timeline';
import { isBefore } from 'utils/date-helper';
import { fromJSDate } from 'utils/luxon-helpers';
import * as hoursTypes from './hours.types';
import type { Filter } from './timeline.types';
import * as types from './timeline.types';
import { timelineXhrModule } from './timeline.types';

interface FetchSuggestedEntries {
  date: string;
  /** Passed XHR key to xhrStatus checking is in-sync across `fetchSuggestedEntries` and `initializeMemory`  */
  xhrKey: string;
}

/**
 * Fetch memory groups /w embedded memories for a given date
 */
export const fetchSuggestedEntries =
  ({ date, xhrKey }: FetchSuggestedEntries): Thunk =>
  async (dispatch, getState) => {
    // Exclude Timely-sourced tracker when dev setting is enabled.
    const trackedDevExclusion = getExcludeTrackerEntriesFromTimelyApiRequest(getState());
    const excludeTrackerEntries = trackedDevExclusion ? 'all' : null;

    dispatch(types.fetchingSuggestedEntries({ date }));

    const entries = await dispatch(
      timelineXhrModule.wrapFetching(
        api.getSuggestedEntries(date, {
          exclude_tracker_entries: excludeTrackerEntries,
        }),
        xhrKey
      )
    );
    const { fetchingForDate } = getTimelineResultMeta(getState());
    if (fetchingForDate !== date) {
      // Another fetch started since this fetch, abort
      return;
    }

    dispatch(
      types.fetchedSuggestedEntries({
        entries,
        date,
        excludeTrackerEntries: Array.isArray(excludeTrackerEntries)
          ? excludeTrackerEntries.join(',')
          : excludeTrackerEntries,
      })
    );
  };

interface InitializeMemory {
  date: string;
}

/**
 * Initializes Memory (unless already up-to-date)
 * Has 5 minute cache timeout + cache invalidation via Pusher
 */
export function initializeMemory({ date }: InitializeMemory): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const lastPusherUpdate = getLastPusherUpdate(state);

    const { fetchedForDate } = getTimelineResultMeta(state);
    const excludeTrackerEntries = getExcludeTrackerEntriesFromTimelyApiRequest(state);

    // Stable XHR key passed to `fetchSuggestedEntries`
    const xhrHash = Object.values({ date, excludeTrackerEntries });
    const xhrKey = `suggestedEntries-${xhrHash.join('-')}`;
    const suggestedEntriesXhrState = getTimelineXhrStatusStateByKey(state, { key: xhrKey });

    const { lastFetch: lastFetchForDate, isLoading: isLoadingDataForDate } =
      suggestedEntriesXhrState;

    const hasDataForDate = fetchedForDate === date;
    const isAlreadyFetching = isLoadingDataForDate;

    const isDataOutdated =
      !!lastFetchForDate &&
      !!lastPusherUpdate &&
      isBefore(fromJSDate(lastFetchForDate).toISO(), lastPusherUpdate) &&
      // Due to both Timely BE & TIC firing Pusher events when recieving Memories,
      // we sometimes fire redundant re-fetches. Avoid re-fetch if data is <30s old.
      isBefore(lastPusherUpdate, DateTime.now().minus({ seconds: 30 }).toISO());

    const needNewData = !hasDataForDate || isDataOutdated;

    const isSelectedDayWithinMemoryHistoryDaysLimit =
      getIsSelectedDayWithinMemoryHistoryDaysLimit(state);

    if (needNewData && !isAlreadyFetching && isSelectedDayWithinMemoryHistoryDaysLimit) {
      dispatch(fetchSuggestedEntries({ date, xhrKey }));
    }
  };
}

export function pusherUpdateForMemories(_message: PusherMessage): Thunk {
  return dispatch => {
    // If MemorySidebar is visible, it'll take care of the updating.
    dispatch(types.suggestedEntriesPusherUpdate());
  };
}

interface SetFilter<TKey extends keyof Filter> {
  key: TKey;
  value: Filter[TKey];
}
export function setFilter<TKey extends keyof Filter>(payload: SetFilter<TKey>): Thunk {
  return dispatch => {
    dispatch(types.setFilter(payload));

    // Remember some preferences, overrides store properties in selectors/timeline::getFilter
    if (isTimelineFilterToRemember(payload.key)) {
      dispatch(
        saveUserProperty(
          payload.key as TimelineFilterToRemember,
          payload.value as any /* TODO PickByValue */
        )
      );
    }
  };
}

export function toggleExpandGroupRef(groupRef: string) {
  return types.toggleExpandGroupRef(groupRef);
}

export function toggleDisplayRuleForMode(statusKey: ValidGroupStatus): Thunk {
  return (dispatch, getState) => {
    const filter = getFilter(getState());

    const key = filter.mode === 'timeline' ? 'displayTimelineRules' : 'displayGroupRules';
    const currentRules = filter[key];

    const value = currentRules.includes(statusKey)
      ? currentRules.filter(status => status !== statusKey)
      : [...currentRules, statusKey];

    dispatch(setFilter({ key, value }));
  };
}

export function toggleSelectEntryIds(entry_ids: Array<number> = [], dragged = false): Thunk {
  return (dispatch, getState) => {
    dispatch(types.toggleSelectEntryIds({ entry_ids }));

    const state = getState();
    const { entryIds: selectedIds } = getTimelineSelection(state);

    // Only track when at least one of the entry ids became selected
    if (entry_ids.some(id => selectedIds.includes(id))) {
      TrackingActions.addedMemoryToTimeEntry(state, dragged);
    }
  };
}

export function clearSelectEntryIds() {
  return types.clearSelectedEntryIds();
}

export function deleteEntryIds(ids: Array<number> | number = []): Thunk {
  return dispatch => {
    const entry_ids = Array.isArray(ids) ? ids : [ids];
    dispatch(types.deleteEntryIds({ entry_ids }));

    // Should probably deal with errors/etc here, but real API endpoint
    // needs to exist before I spend time on this.
    return api.deleteEntryIds(entry_ids);
  };
}

export function toggleExpandEntryId(entryId: number) {
  return types.toggleExpandEntryId(entryId);
}

export function markEntryIdsAsSpam(entryIds: Array<number> | number = []): Thunk {
  const ids = Array.isArray(entryIds) ? entryIds : [entryIds];

  return (dispatch, getState) => {
    dispatch(types.markEntryIdsAsSpam({ entry_ids: ids }));

    const hasIgnoredMemory = getUserPropertyAsNumber(getState(), 'has_ignored_memory');
    if (!hasIgnoredMemory) {
      dispatch(saveUserProperty('has_ignored_memory', 1));
    }

    api.markEntryIdsAsSpam(ids).catch(() => {
      dispatch(showToast({ message: 'Could not ignore memories', type: 'danger' }));
      dispatch(types.markEntryIdsAsNotSpam({ entry_ids: ids }));
    });
  };
}

/**
 * Remove spam flag on memory/entry ids'
 *
 * Todo: Re-consider merging this with markEntryIdsAsSpam when we know the complexities involved.
 */
export function markEntryIdsAsNotSpam(entryIds: Array<number> | number = []): Thunk {
  const ids = Array.isArray(entryIds) ? entryIds : [entryIds];

  return dispatch => {
    dispatch(types.markEntryIdsAsNotSpam({ entry_ids: ids }));

    api.markEntryIdsAsNotSpam(ids).catch(() => {
      dispatch(showToast({ message: 'Could not restore memories', type: 'danger' }));
      dispatch(types.markEntryIdsAsSpam({ entry_ids: ids }));
    });
  };
}

export function ensureEntryIdsAreNotMarkedAsSpam(entryIds: Array<number>): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const memories = getTimelineMemories(getState()).filter(memory => entryIds.includes(memory.id));
    const toRestore = memories.filter(memory => memory.isSpam);
    if (toRestore.length < 1) {
      return Promise.resolve();
    }
    const ids = toRestore.map(memory => memory.id);
    dispatch(types.markEntryIdsAsNotSpam({ entry_ids: ids }));
    try {
      await api.markEntryIdsAsNotSpam(ids);
    } catch (e) {
      dispatch(types.markEntryIdsAsSpam({ entry_ids: ids }));
    }
  };
}

interface PredictEntryIdsToProjectId {
  entryIds: Array<number> | number;
  projectId: number;
}
export function predictEntryIdsToProjectId({
  entryIds,
  projectId,
}: PredictEntryIdsToProjectId): Thunk {
  const ids: Array<number> = Array.isArray(entryIds) ? entryIds : [entryIds];
  return (dispatch, getState) => {
    // Find the group.ref's to apply the predictions to (since we can't set directly on memories)
    // Prediction is made for a group which contain ANY of the entryIds passed.
    const groupRefs = getMemoryGroups(getState())
      .filter(group => intersection(group.entry_ids, ids).length > 0)
      .map(({ ref }) => ref);

    // Inject project prediction into suggested_entry.projects as first project
    // Potential issues: See https://memoryai.slack.com/archives/CAMBUQ7TM/p1549007662051600
    dispatch(types.predictGroupRefsToProjectId({ groupRefs, projectId }));

    // Clear the selection
    // dispatch(clearSelectEntryIds());

    // Notify Hour draft that we'll want to remove any entry_ids that got predicted
    // ... unless the projectId matches what's already in draft.
    dispatch(
      hoursTypes.conditionallyRemoveEntryIdsOnHourDraft({
        entry_ids: ids,
        ignoreForProjectId: projectId,
      })
    );

    return api.predictEntryIdsToProjectId(ids, projectId).catch(() => {
      dispatch(showToast({ message: 'Could not predict memories to draft', type: 'danger' }));
    });
  };
}

export const preserveScrollPosition = (position: number | null) =>
  types.preserveScrollPosition(position);

const createDebouncedFn = <T extends (...args: any[]) => void>(fn: T, timeout: number) => {
  let timeoutId: number | undefined;
  return (...args: Parameters<T>) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      fn(...args);
    }, timeout);
  };
};

/**
 * Because setPixelsPerMinute is called every time the user changes the zoom level, this can cause
 * a race condition. For example if the user zooms 3 times, and we send requests A - B - C, they
 * might arrive on the backend in the order of A - C - B. So when the user reloads the page, they
 * will see the wrong zoom level.
 */
const debouncedSetPixelsPerMinuteUserProperty = createDebouncedFn(
  (pixelsPerMinute: number, dispatch: ThunkDispatch) => {
    dispatch(saveUserProperty(TIMELINE_pixelsPerMinute, pixelsPerMinute));
  },
  1000
);

export const setPixelsPerMinute =
  (pixelsPerMinute: number): Thunk<void> =>
  dispatch => {
    dispatch(types.setPixelsPerMinute(pixelsPerMinute));
    debouncedSetPixelsPerMinuteUserProperty(pixelsPerMinute, dispatch);
  };

/**
 * @see debouncedSetPixelsPerMinuteUserProperty
 */
const debouncedSetTimestampSnappingMinutesUserProperty = createDebouncedFn(
  (minutes: number, dispatch: ThunkDispatch) => {
    dispatch(saveUserProperty(TIMESTAMP_SNAPPING_MINUTES, minutes));
  },
  1000
);
export const setTimestampSnappingMinutes =
  (minutes: number): Thunk<void> =>
  dispatch => {
    dispatch(types.setTimestampSnappingMinutes(minutes));
    debouncedSetTimestampSnappingMinutesUserProperty(minutes, dispatch);
  };

export const interactWithAiIgnoredMemoriesHint = (): Thunk => {
  return dispatch => {
    dispatch(saveUserProperty(HAS_SEEN_AI_IGNORED_MEMORIES_HINT, true));
    dispatch(types.setShowAiIgnoredMemoriesModal(true));
  };
};
